import liCSS from 'components/Shared/MakeModelMenu/liCSS';
import { colors } from 'components/styles/variables';
import Model from 'types/Model';
import useEvents from 'lib/hooks/useEvents';
import { ModelTap } from 'lib/Tracking/MakeModelMenu/MakeModelMenuEvents';
import { useContext, useMemo } from 'react';
import { FilterEventContext } from 'contexts/FilterEventContext';
import { useMultiMakeModelFilter } from 'lib/hooks/useSrpFilters';
import selectedCheckIcon from 'public/static/images/srp/check-disabled.svg';
import { formatNumberWithCommas } from 'lib/utils';

interface ModelListResultsProps {
  models: Model[];
  choose: (value: Model) => void;
  modelFacets?: Record<string, number>;
}

interface ResultProps {
  model: Model;
  choose: (value: Model) => void;
  modelFacet?: number;
}

function Result({ model, choose, modelFacet }: ResultProps): JSX.Element {
  const logger = useEvents();
  const filterEventContext = useContext(FilterEventContext);

  const { value } = useMultiMakeModelFilter();

  const selectedModels = useMemo(() => {
    if (!value) return [];

    return value.reduce((uniqueModels: string[], item) => {
      if (item && item.model && !uniqueModels.includes(item.model)) {
        uniqueModels.push(item.model);
      }
      return uniqueModels;
    }, []);
  }, [value]);

  const disabled = selectedModels.includes(model.name);
  const zeroFacets = modelFacet === undefined || modelFacet === 0;

  const formattedFacetCount = useMemo(() => {
    return zeroFacets ? 0 : formatNumberWithCommas(modelFacet as number);
  }, [zeroFacets, modelFacet]);

  const className = useMemo(() => {
    return `make-model-menu-model-list-results-item ${disabled ? 'disabled' : ''} ${
      zeroFacets ? 'zero-facets' : ''
    }`;
  }, [disabled, zeroFacets]);

  function handleClick(event: React.MouseEvent<HTMLLIElement>): void {
    if (disabled || zeroFacets) {
      event.stopPropagation();
      return;
    }
    logger.log(ModelTap(filterEventContext));
    if (choose) {
      choose(model);
    }
  }

  return (
    <li key={model.name} data-model={model.name} className={className} onClick={handleClick}>
      <div className="name">{model.name}</div>
      <div className="facet-count">{`(${formattedFacetCount})`}</div>
      {disabled && <div className="selected-icon" />}
      <style jsx>{liCSS}</style>
      <style jsx>{`
        .zero-facets {
          color: ${colors.G2};
          cursor: not-allowed;
          opacity: 0.7;
        }
        .facet-count {
          padding-left: 4px;
        }
        .make-model-menu-model-list-results-item {
          display: flex;
          align-items: center;
          padding-left: 24px;
        }
        .name {
          font-size: 14px;
          line-height: 20px;
        }
        .badge {
          display: none;
          font-size: 9px;
          color: white;
          background-color: ${colors.G2};
          border-radius: 10px;
          position: relative;
          text-transform: uppercase;
          font-weight: 600;
          padding: 4px 10px;
          top: 1px;
          left: 10px;
        }
        .make-model-menu-model-list-results-item.--disabled .badge {
          display: inline-block;
        }
        .make-model-menu-model-list-results-item.disabled {
          color: ${colors.G3};
        }
        .make-model-menu-model-list-results-item.disabled:hover {
          cursor: default;
        }
        .selected-icon {
          background-image: url(${selectedCheckIcon});
          color: ${colors.G3};
          width: 17px;
          height: 17px;
          background-size: contain;
          background-repeat: no-repeat;
          position: absolute;
          right: 14px;
          bottom: 16px;
        }
      `}</style>
    </li>
  );
}

function ModelListResults({ models, choose, modelFacets }: ModelListResultsProps): JSX.Element {
  return (
    <>
      {models.map((model) => (
        <Result
          model={model}
          key={model.name}
          choose={choose}
          modelFacet={modelFacets?.[model.name]}
        />
      ))}
      <style jsx>{liCSS}</style>
      <style jsx>
        {`
          .make-model-menu-models-list {
            margin: 0;
            padding: 0;
          }
        `}
      </style>
    </>
  );
}

export default ModelListResults;
